import React, { useState, useEffect } from 'react'
import { Route, useHistory, useLocation } from 'react-router-dom'
import { Home } from '../home/Index'
import { Scheduler } from '../scheduler/Index'
import { Application } from '../application/Index'
import { Account } from '../account/Index'
import { OrganizationSearch } from '../organization/search/Index'
import { CreateOrganization } from '../organization/create/Index'
import { OrganizationBulkInsert } from '../organization/bulkInsert/Index'
import { Permissions } from '../../permissions'
import { LoadingFactory } from '../../components/LoadingFactory'
import { Tenant } from 'connection/models/Tenant'
import { UserData } from 'connection/models/UserData'
import { Images } from '../application/images/Index'
import { Statuspage } from '../statuspage/Statuspage'
import { IncidentCreationPage } from '../statuspage/create/IncidentCreationPage'
import { PermissionService } from 'services/PermissionService'
import { PermissionDenied } from '../../views/permissionDenied/Index'
import { Procedure } from '../procedure/Index'
import SsoComponent from '../organization/sso/Index'
import { SendMessage } from '../messages'
import { AdminIdentity } from '../adminIdentity/Index'
import { CloudRecommendation } from '../cloud/CloudRecommendation'
import { ResetMfaComponent } from '../organization/resetMfa/Index'

interface ApplicationRoutesProps {
  tenant: Tenant;
  userAuth: UserData;
}

const HOME_PATH_NAME = '/';
const PERMISSION_DENIED_PATH_NAME = '/permissiondenied';

export const ApplicationRoutes = ({ tenant, userAuth }: ApplicationRoutesProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation();
  const history = useHistory();

  const groupPermissions = [
    {
      path: '/organization/search',
      rule: Permissions.Organization_Search__Read,
    },
    {
      path: '/organization/create',
      rule: Permissions.Organization_Create
    },
    {
      path: '/organization/bulkInsert',
      rule: Permissions.Organization_Create
    },
    {
      path: '/organization/team/organizationteam',
      rule: Permissions.Organization_Team__Read
    },
    {
      path: '/organization/sso',
      rule: Permissions.Organization_Sso_Add
    },
    {
      path: '/organization/reset-mfa',
      rule: Permissions.Organization_Reset_Mfa
    },
    {
      path: '/application',
      rule: Permissions.Application_Overview__Read
    },
    {
      path: '/application/clone',
      rule: Permissions.Application_Clone
    },
    {
      path: '/application/campaign',
      rule: Permissions.Application_Overview__Edit
    },
    {
      path: '/application/migrate',
      rule: Permissions.Application_Migrate
    },
    {
      path: '/application/analytics',
      rule: Permissions.Application_Analytics
    },
    {
      path: '/application/configurations',
      rule: Permissions.Application_Configurations__Read
    },
    {
      path: '/application/team',
      rule: Permissions.Application_Team
    },
    {
      path: '/application/builder',
      rule: Permissions.Application_Builder
    },
    {
      path: '/application/contacts',
      rule: Permissions.Application_Contacts_Info
    },
    {
      path: '/application/desk',
      rule: Permissions.Application_Desk
    },
    {
      path: '/application/bothealth',
      rule: Permissions.Application_Overview__Read
    },
    {
      path: '/application/sms',
      rule: Permissions.Application_Channels_Sms_Read
    },
    {
      path: '/application/whatsapp',
      rule: Permissions.Application_Overview__Read
    },
    {
      path: '/statuspage',
      rule: Permissions.Application_StatusPage__Read
    },
    {
      path: '/messages',
      rule: Permissions.Application_Messages__Read
    },
    {
      path: '/statuspage/create',
      rule: Permissions.Application_StatusPage__Edit
    },
    {
      path: '/scheduler',
      rule: Permissions.Scheduler
    },
    {
      path: '/images',
      rule: Permissions.Images
    },
    {
      path: '/account',
      rule: Permissions.Account
    },
    {
      path: '/billing',
      rule: Permissions.Billing
    },
    {
      path: '/adminidentity',
      rule: Permissions.Admin_Identity_Access_Key
    },
    {
      path: '/cloud',
      rule: Permissions.Application_Cloud
    },
    {
      path: '/procedure',
      rule: Permissions.Procedures
    },
  ];

  const checkIfHasPermission = () => {
    if (location.pathname === HOME_PATH_NAME || location.pathname === PERMISSION_DENIED_PATH_NAME) {
      return;
    }
    const pathname = location.pathname.replace(/\/scheduler\/.*$/, '/scheduler');
    const permission = groupPermissions.find(group => group.path === pathname);
    if (!permission || (permission && !PermissionService.hasPermission(permission.rule))) {
      history.push(PERMISSION_DENIED_PATH_NAME);
    }
  }

  useEffect(() => {
    checkIfHasPermission();
    setIsLoading(false);
  }, [location]);

  return isLoading
    ? <LoadingFactory />
    : (
      <div>
        <Route
          path="/"
          exact
          component={Home}
        />
        <Route
          path="/permissiondenied"
          exact
          component={PermissionDenied}
        />
        <Route
          path="/scheduler/:identity?/:userEmail?/:userName?/:locale?"
          exact
          component={Scheduler}
        />
        <Route
          path="/messages"
          exact
          component={SendMessage}
        />
        <Route
          path="/statuspage"
          exact
          component={Statuspage}
        />
        <Route
          path="/statuspage/create"
          exact
          component={IncidentCreationPage}
        />
        <Route
          path="/application/:application?"
          exact
          component={Application}
        />
        <Route
          path="/account"
          exact
          component={Account}
        />
        <Route
          path="/images"
          exact
          component={Images}
        />
        <Route
          path="/organization/search"
          exact
          component={OrganizationSearch}
        />
        <Route
          path="/organization/create"
          exact
          component={CreateOrganization}
        />
        <Route
          path="/organization/bulkInsert"
          exact
          component={OrganizationBulkInsert}
        />
        <Route
          path="/organization/team/:organizationteam"
          exact
          component={Application}
        />
        <Route
          path="/organization/sso"
          exact
          component={SsoComponent}
        />
        <Route
          path="/adminidentity"
          exact
          component={AdminIdentity}
        />
        <Route
          path="/cloud"
          exact
          component={CloudRecommendation}
        />
        <Route
          path="/procedure"
          exact
          component={Procedure}
        />
        <Route
          path="/organization/reset-mfa"
          exact
          component={ResetMfaComponent}
        />
      </div>
    )
}
